/**
 * Tooltip system
 *
 * Lightweight hover tooltips for UI elements.
 * Call `attachTooltip(element, text)` to add a tooltip to any DOM element.
 * The tooltip follows the cursor and dismisses on mouseleave.
 */

let tip: HTMLDivElement | null = null;
let currentTarget: EventTarget | null = null;

// ── Internal tooltip DOM ───────────────────────────────────────────────────────

function getTip(): HTMLDivElement {
  if (!tip) {
    tip = document.createElement('div');
    tip.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 9999;
      background: rgba(8,12,24,0.95);
      border: 1px solid rgba(100,140,255,0.45);
      border-radius: 5px;
      padding: 6px 10px;
      font-family: 'Courier New', monospace;
      font-size: 11px;
      color: #a0b8e0;
      max-width: 240px;
      line-height: 1.45;
      white-space: pre-wrap;
      box-shadow: 0 4px 16px rgba(0,0,0,0.6);
      display: none;
    `;
    document.body.appendChild(tip);
  }
  return tip;
}

let cachedTipW = 0;
let cachedTipH = 0;
let cachedTipText = '';

function show(text: string, x: number, y: number): void {
  const t = getTip();
  const textChanged = text !== cachedTipText;
  if (textChanged) {
    t.textContent = text;
    cachedTipText = text;
  }
  if (t.style.display === 'none' || textChanged) {
    t.style.display = 'block';
    // Only read layout when text changed or tooltip was hidden
    cachedTipW = t.offsetWidth || 200;
    cachedTipH = t.offsetHeight || 40;
  }
  // Position tooltip above and to the right of cursor, nudge if near edge
  const margin = 12;
  let left = x + margin;
  let top = y - 8;
  if (left + cachedTipW > window.innerWidth - 8) left = x - cachedTipW - margin;
  if (top + cachedTipH > window.innerHeight - 8) top = y - cachedTipH - margin;
  if (top < 8) top = 8;
  t.style.left = `${left}px`;
  t.style.top = `${top}px`;
}

function hide(): void {
  if (tip) tip.style.display = 'none';
  currentTarget = null;
  cachedTipText = '';
}

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Attach a tooltip to any HTMLElement.
 * @param el  The element that triggers the tooltip on hover.
 * @param text  The tooltip text (may contain newlines).
 */
export function attachTooltip(el: HTMLElement, text: string): void {
  el.addEventListener('mouseenter', (e) => {
    currentTarget = e.currentTarget;
    show(text, e.clientX, e.clientY);
  });

  el.addEventListener('mousemove', (e) => {
    if (currentTarget === e.currentTarget) {
      show(text, e.clientX, e.clientY);
    }
  });

  el.addEventListener('mouseleave', (e) => {
    if (currentTarget === e.currentTarget) hide();
  });
}

/**
 * Attach a tooltip with a dynamic text provider (re-evaluated on each hover).
 */
export function attachDynamicTooltip(el: HTMLElement, getText: () => string): void {
  let text = '';

  el.addEventListener('mouseenter', (e) => {
    currentTarget = e.currentTarget;
    text = getText();
    show(text, e.clientX, e.clientY);
  });

  el.addEventListener('mousemove', (e) => {
    if (currentTarget === e.currentTarget) {
      show(text, e.clientX, e.clientY);
    }
  });

  el.addEventListener('mouseleave', (e) => {
    if (currentTarget === e.currentTarget) hide();
  });
}

/** Dismiss any visible tooltip immediately. */
export function hideTooltip(): void {
  hide();
}
